import axios, { AxiosError } from 'axios';

import { ExcursionPrepared, ExcursionType, GetExcursionRes, GetExcursionsRes, GetGuidesRes, GetUnauthExcursionRes } from './types';
import { EMarkerImage, IMarkerProps } from 'components/Marker';
import { prepareExcursion } from './../hooks/useExcursion';
import { AuthError } from 'errors';
import userApi from './userApi';

const instance = axios.create({
    baseURL: 'https://st.2aw.io:8081/',
    headers: { 'Content-Type': 'application/json; charset=UTF-8' },
})

const excursionAPI = {
    join(uid: string, sid: string, ex_tid: number) {
        return instance.patch(
            `/excursions?uid=${uid}&sid=${sid}&ex_tid=${ex_tid}&attach_me=1`,
        );
    },

    async getMarkers(
        uid: string, 
        sid: string, 
        data: object, 
        t: (code: string) => string, 
        handler: (event: React.MouseEvent, place: ExcursionPrepared) => void,
        errorCallback: () => void
    ) {
        return await instance.post<any, GetGuidesRes>(
            `/guides/show?uid=${uid}&sid=${sid}&action=guides_on_map&filter=custom`,
            data
        ).then((response) => {
            if (response.data && response.data.guide) {
                return prepareMarkers(uid, sid, response, t, handler, errorCallback);
            } else {
                return [];
            }
        }).catch((error) => {
            if (error.response.data.status) {
                switch (error.response.data.status) {
                    case 'user not found':
                    case 'bad sid':
                        throw new AuthError();
                }
            }

            throw new Error('Error');
        });;
    },

    getExcursions(uid?: string, sid?: string, page?: number) {
        page = page || 0;
        var url = `/excursions/find?page=${page}`;

        if (uid && sid) {
            url = `/excursions/find?uid=${uid}&sid=${sid}&page=${page}`;
        }
        return instance
            .get<any, GetExcursionsRes>(url)
            .then((response) => {
                return response;
            });
    },

    getExcursionsMarkers(t: (code: string) => string, handler: (event: React.MouseEvent, place: ExcursionType) => void) {
        return instance.get<any, GetExcursionsRes>(
            `/excursions/find?page=0`
        ).then((response) => {
            return prepareExcursionsMarkers(t, response, handler);
        });
    },

    getRoutes(lat: number, lng: number, page?: number) {
        page = page || 0;
        return instance.get<any, GetExcursionsRes>(
            `/excursions/find?page=${page}&lattitude=${lat}&longitude=${lng}&filter=routes`
        ).then((response) => {
            return response.data.excursions
                ? response.data.excursions.map((place: ExcursionType) => {
                    return prepareExcursion(place);
                })
                : [];
        });
    },

    deleteRoute(uid: string, sid: string, ex_tid: number) {
        return instance.patch<any, GetExcursionsRes>(
            `/excursions?uid=${uid}&sid=${sid}&ex_tid=${ex_tid}&status=deleted_by_guide`
        );
    },

    getExcursion(uid: string, sid: string, ex_tid: number) {
        return instance.get<any, GetExcursionRes>(`/excursions?uid=${uid}&sid=${sid}&by_tid=${ex_tid}`)
    },

    getExUnauth(ex_tid: number) {
        return instance.get<any, GetUnauthExcursionRes>(`/excursions/find?by_tid=${ex_tid}&page=0`)
            .then(res => res.data)
    },

    rate(uid: string, sid: string, ex_tid: number, rate: number) {
        return instance.patch(`/excursions?uid=${uid}&sid=${sid}&ex_tid=${ex_tid}&rate=${rate}`)
    },

    async patchExcursionStart(uid: string, sid: string, ex_tid: number) {
        return instance.patch(`/excursions?uid=${uid}&sid=${sid}&ex_tid=${ex_tid}&status=grp_start_by_user`)
            .catch((err: AxiosError) => {
                console.error(err)
                return err.response
            })
    },

    async patchExcursionAccepted(uid: string, sid: string, ex_tid: number) {
        return instance.patch<{ status: string }>(`/excursions?uid=${uid}&sid=${sid}&ex_tid=${ex_tid}&status=grp_accepted_by_user`)
            .catch((err: AxiosError) => {
                console.error(err)
                return err.response
            })
    },

    create(uid: string, sid: string, route: any) {
        return instance.post<{ status: string, TID_list: string[] }>(
            `/excursions?uid=${uid}&sid=${sid}&action=new_excursion`,
            JSON.stringify({
                'routes': [route]
            })
        )
            .catch((err: AxiosError) => {
                console.error(err)
                return err.response
            })
    },

    addFiles(uid: string, sid: string, tid: string, files: File[]) {
        files.map((file, index) => {
            instance.post<{ status: string }>(
                `/pictures?uid=${uid}&sid=${sid}&for_excursion=${tid}&image_number=${index}`, file, {
                headers: {
                    'Content-Type': 'image/jpeg',
                }
            })
                .catch((err: AxiosError) => {
                    console.error(err);
                })
        });
    }
}

const prepareMarkers = (
    uid: string, 
    sid: string, 
    places: GetGuidesRes, 
    t: (code: string) => string, 
    handler: (event: React.MouseEvent, place: ExcursionPrepared) => void, 
    errorCallback: () => void
): IMarkerProps[] => {
    return places.data.guide.map((place: any) => {
        const [lat, lng, count, free, radius, euid, isGroup, tid, isOnline, isRoute] = place;

        if (count > 1) {
            return {
                key: lat.toString() + lng.toString() + Math.random(),
                count: count,
                type: 'group',
                radius: radius,
                text: euid,
                image: EMarkerImage.group,
                lat: lat,
                lng: lng,
            };
        } else if (isRoute) {
            return prepareRouteMarker(uid, sid, place, t, handler, errorCallback);
        } else if (isGroup === '1') {
            return prepareExcursionMarker(uid, sid, place, t, handler, errorCallback);
        } else {
            return prepareGuideMarker(uid, sid, place, errorCallback);
        }
    });
}

const prepareRouteMarker = (
    uid: string, 
    sid: string, 
    info: any, 
    t: (code: string) => string, 
    handler: (event: React.MouseEvent, place: ExcursionPrepared) => void, 
    errorCallback: () => void
): IMarkerProps => {
    const [lat, lng, count, free, radius, euid, isGroup, tid, isOnline, isRoute] = info;

    return {
        key: lat.toString() + lng.toString() + Math.random(),
        count: 1,
        type: 'route',
        image: EMarkerImage.route,
        lat: lat,
        lng: lng,
        infoGetter: () => {
            return excursionAPI.getExcursion(uid, sid, tid)
                .then((res) => {
                    if (res && res.data.excursions) {
                        return getRouteInfo(t, res.data.excursions[0], handler);
                    }
                })
                .catch(() => { errorCallback(); return undefined });
        }
    };
};

const prepareExcursionMarker = (
    uid: string, 
    sid: string, 
    info: any, 
    t: (code: string) => string, 
    handler: (event: React.MouseEvent, place: ExcursionPrepared) => void, 
    errorCallback: () => void
): IMarkerProps => {
    const [lat, lng, count, free, radius, euid, isGroup, tid, isOnline, isRoute] = info;

    return {
        key: tid.toString(),
        count: 1,
        type: 'excursion',
        image: isOnline ? EMarkerImage.excursion : EMarkerImage.excursion_planed,
        lat: lat,
        lng: lng,
        infoGetter: () => {
            return excursionAPI.getExcursion(uid, sid, tid)
                .then((res) => {
                    if (res && res.data.excursions) {
                        return getExcursionInfo(t, res.data.excursions[0], handler);
                    }
                })
                .catch(() => { errorCallback(); return undefined });
        }
    };
};

const prepareGuideMarker = (
    uid: string, 
    sid: string, 
    info: any, 
    errorCallback: () => void
): IMarkerProps => {
    const [lat, lng, count, free, radius, euid, isGroup, tid, isOnline, isRoute] = info;

    const result: IMarkerProps = {
        key: lat.toString() + lng.toString() + Math.random(),
        count: 1,
        type: 'guide',
        radius: radius,
        image: EMarkerImage.guide,
        lat: lat,
        lng: lng,
        infoGetter: () => {
            return userApi.getGuidInfo(uid, sid, euid)
                .then((res) => {
                    if (res) {
                        return {
                            title: res.user_euid,
                            text: res.user_about,
                            imageUrl: res.images[0].image_url,
                            action: {
                                text: '',
                                callback: () => { },
                            }
                        }
                    }
                })
                .catch(() => { errorCallback(); return undefined });
        }
    };

    return result;
};

const prepareExcursionsMarkers = (t: (code: string) => string, places: GetExcursionsRes, handler: (event: React.MouseEvent, place: ExcursionType) => void): IMarkerProps[] => {

    return places.data.excursions
        ? places.data.excursions.map((place: ExcursionType) => {
            const placePrepared = prepareExcursion(place);
            const excursionInfo = getExcursionInfo(t, place, handler);

            return {
                key: place.ex_tid.toString(),
                count: 1,
                type: 'excursion',
                text: placePrepared.ex_description,
                image: placePrepared.isOnline ? EMarkerImage.excursion : EMarkerImage.excursion_planed,
                lat: place.ex_user_points[0].lattitude,
                lng: place.ex_user_points[0].longitude,
                infoGetter: () => {
                    return excursionInfo;
                }
            };
        })
        : [];
};

const getRouteInfo = (t: (code: string) => string, place: ExcursionType, handler: (event: React.MouseEvent, place: ExcursionPrepared) => void) => {
    const placePrepared = prepareExcursion(place);

    let text = `
        <div class="marker__info-text-item">
            ${t('GUIDE')}:

            <span class="marker__info-prop">
                ${place.ex_guide} 
            </span>
        </div>
    `;

    return {
        isPlaned: !placePrepared.isOnline,
        title: placePrepared.ex_name,
        subTitle: placePrepared.ex_description,
        text: text,
        imageUrl: placePrepared.firstImageUrl,
        redirectLink: placePrepared.ex_url_formatted + '?trvl=' + placePrepared.ex_tid,
        action: {
            text: t('START.ROUTE'),
            callback: (event: React.MouseEvent) => { handler(event, placePrepared) },
        }
    };
}

const getExcursionInfo = (t: (code: string) => string, place: ExcursionType, handler: (event: React.MouseEvent, place: ExcursionPrepared) => void) => {

    const placePrepared = prepareExcursion(place);

    let text = `
        <div class="marker__info-text-item">
            ${t('GUIDE')}:

            <span class="marker__info-prop">
                ${place.ex_guide} 
            </span>
        </div>
    `;

    if (!placePrepared.isOnline) {
        text += `
            <div class="marker__info-text-item"> 
                ${t('START')}:
                
                <span class="marker__info-prop">
                ${placePrepared.start_time}
                </span>
            </div>
        `;
    }
    return {
        isPlaned: !placePrepared.isOnline,
        title: placePrepared.ex_name,
        subTitle: placePrepared.ex_description,
        text: text,
        imageUrl: placePrepared.firstImageUrl,
        action: {
            text: placePrepared.isOnline ? t('WATCH') : t('JOIN'),
            callback: (event: React.MouseEvent) => { handler(event, placePrepared) },
        }
    };
};

export default excursionAPI
